const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Helpers ──────────────────────────────────────────────────────
function makePath(pts, tx, ty, clip) {
  let d = ''; let pen = false;
  for (const [x, y] of pts) {
    const px = tx(x), py = ty(y);
    if (clip && (py < clip[0] || py > clip[1])) { pen = false; continue; }
    d += (pen ? 'L' : 'M') + px.toFixed(1) + ' ' + py.toFixed(1) + ' ';
    pen = true;
  }
  return d;
}

// ── Diagram 1: Time Domain → Frequency Domain ───────────────────
function DiagramTimeFreq() {
  const W = 900, H = 370;

  // Left panel: composite time-domain signal
  // f(t) = 1.0·sin(2πt) + 0.6·sin(6πt) + 0.3·sin(10πt)
  const freqs = [1, 3, 5];
  const amps  = [1.0, 0.6, 0.3];
  const colors = ['#2E75B6', '#27ae60', '#c0392b'];

  const lW = 390, lH = 300, lX = 10, lY = 35;
  const ox = lX + 46, oy = lY + lH / 2, scx = (lW - 56) / 2.2, scy = 90;
  const tx = t => ox + t * scx;
  const ty = y => oy - y * scy;

  // composite
  const compPts = [];
  for (let t = 0; t <= 2.2; t += 0.01) {
    const y = amps.reduce((s, a, i) => s + a * Math.sin(2 * Math.PI * freqs[i] * t), 0);
    compPts.push([t, y]);
  }

  // individual components
  const compIndiv = freqs.map((f, i) => {
    const pts = [];
    for (let t = 0; t <= 2.2; t += 0.01)
      pts.push([t, amps[i] * Math.sin(2 * Math.PI * f * t)]);
    return pts;
  });

  // Right panel: frequency spectrum
  const rX = 470, rW = 420, rH = 300, rY = 35;
  const fox = rX + 50, foy = rY + rH - 30, fscx = (rW - 60) / 8, fscy = 200;
  const ftx = f => fox + f * fscx;
  const fty = a => foy - a * fscy;

  const arrowH = 18;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Core Idea: Decomposing a Signal from Time Domain into Frequency Domain</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── Left: Time domain ── */}
        <rect x={lX} y={lY} width={lW} height={lH} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={lX + lW/2} y={lY - 8} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Time Domain  f(t)</text>

        {/* Axes */}
        <line x1={ox} y1={ty(-1.8)} x2={ox} y2={ty(1.8)} stroke="#1F3864" strokeWidth="1.4" />
        <line x1={ox} y1={oy} x2={tx(2.2)} y2={oy} stroke="#1F3864" strokeWidth="1.4" />
        <polygon points={`${tx(2.2)},${oy} ${tx(2.2)-7},${oy-3} ${tx(2.2)-7},${oy+3}`} fill="#1F3864"/>
        <polygon points={`${ox},${ty(1.8)} ${ox-3},${ty(1.8)+7} ${ox+3},${ty(1.8)+7}`} fill="#1F3864"/>
        <text x={tx(2.2)+5} y={oy+4} fontSize="12" fill="#1F3864" fontWeight="700">t</text>
        <text x={ox+4} y={ty(1.8)-4} fontSize="12" fill="#1F3864" fontWeight="700">f(t)</text>
        {[0.5,1.0,1.5,2.0].map(v=>(
          <g key={v}>
            <line x1={tx(v)} y1={oy-3} x2={tx(v)} y2={oy+3} stroke="#bbb" strokeWidth="1"/>
            <text x={tx(v)} y={oy+14} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>
          </g>
        ))}

        {/* Individual components faint */}
        {compIndiv.map((pts, i) => (
          <path key={i} d={makePath(pts, tx, ty, [lY, lY+lH])} fill="none" stroke={colors[i]} strokeWidth="1.2" strokeOpacity="0.4" strokeDasharray="4,3"/>
        ))}

        {/* Composite bold */}
        <path d={makePath(compPts, tx, ty, [lY, lY+lH])} fill="none" stroke="#1F3864" strokeWidth="2.4"/>

        {/* Component labels */}
        {freqs.map((f, i) => (
          <g key={f}>
            <line x1={lX+10} y1={lY + lH + 18 + i*22} x2={lX+30} y2={lY + lH + 18 + i*22} stroke={colors[i]} strokeWidth="2" strokeDasharray="5,3"/>
            <text x={lX+36} y={lY + lH + 22 + i*22} fontSize="11" fill={colors[i]}>{amps[i]}·sin(2π·{f}t)</text>
          </g>
        ))}
        <text x={lX+10} y={lY + lH + 88} fontSize="11" fill="#1F3864" fontWeight="700">━ sum (composite)</text>

        {/* ── Arrow ── */}
        <defs>
          <marker id="arr" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="#9b59b6"/>
          </marker>
        </defs>
        <line x1={lX+lW+8} y1={lY+lH/2} x2={rX-8} y2={rY+rH/2} stroke="#9b59b6" strokeWidth="2.5" markerEnd="url(#arr)"/>
        <text x={(lX+lW+rX)/2} y={lY+lH/2-12} textAnchor="middle" fontSize="12" fill="#9b59b6" fontWeight="700">Fourier</text>
        <text x={(lX+lW+rX)/2} y={lY+lH/2+6} textAnchor="middle" fontSize="12" fill="#9b59b6" fontWeight="700">Transform</text>

        {/* ── Right: Frequency domain ── */}
        <rect x={rX} y={rY} width={rW} height={rH} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={rX+rW/2} y={rY-8} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Frequency Domain  F(ω)</text>

        {/* Freq axes */}
        <line x1={fox} y1={fty(0)} x2={fox} y2={fty(1.15)} stroke="#1F3864" strokeWidth="1.4"/>
        <line x1={fox} y1={foy} x2={ftx(8)} y2={foy} stroke="#1F3864" strokeWidth="1.4"/>
        <polygon points={`${ftx(8)},${foy} ${ftx(8)-7},${foy-3} ${ftx(8)-7},${foy+3}`} fill="#1F3864"/>
        <polygon points={`${fox},${fty(1.15)} ${fox-3},${fty(1.15)+7} ${fox+3},${fty(1.15)+7}`} fill="#1F3864"/>
        <text x={ftx(8)+5} y={foy+4} fontSize="12" fill="#1F3864" fontWeight="700">ω</text>
        <text x={fox+4} y={fty(1.15)-4} fontSize="12" fill="#1F3864" fontWeight="700">|F(ω)|</text>
        {[1,2,3,4,5,6,7].map(v=>(
          <g key={v}>
            <line x1={ftx(v)} y1={foy-3} x2={ftx(v)} y2={foy+3} stroke="#bbb" strokeWidth="1"/>
            <text x={ftx(v)} y={foy+14} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>
          </g>
        ))}
        {[0.5,1.0].map(v=>(
          <text key={v} x={fox-8} y={fty(v)+4} textAnchor="end" fontSize="10" fill="#bbb">{v}</text>
        ))}

        {/* Spectral lines */}
        {freqs.map((f, i) => {
          const bx = ftx(f), by = fty(amps[i]);
          return (
            <g key={f}>
              <line x1={bx} y1={foy} x2={bx} y2={by} stroke={colors[i]} strokeWidth="10" strokeOpacity="0.85"/>
              <text x={bx} y={by-8} textAnchor="middle" fontSize="11" fill={colors[i]} fontWeight="700">{amps[i]}</text>
              <text x={bx} y={foy+26} textAnchor="middle" fontSize="10" fill={colors[i]}>f={f} Hz</text>
            </g>
          );
        })}

        {/* Amplitude axis ticks */}
      </svg>
      <div style={S.dCaption}>The Fourier Transform decomposes any signal into its constituent sinusoids. The time-domain composite (black curve, left) is built from three sine waves at frequencies 1, 3 and 5 Hz. The Fourier Transform reveals exactly those three spikes in the frequency domain (right) — one spike per frequency, with height equal to amplitude. The transform converts a complicated waveform into a simple fingerprint.</div>
    </div>
  );
}

// ── Diagram 2: Fourier Series — partial sums of a square wave ───
function DiagramFourierSeries() {
  const W = 900, H = 380;

  // Square wave Fourier series: f(t) = (4/π) Σ sin((2k-1)t)/(2k-1), k=1,2,3,...
  function squarePartial(t, N) {
    let s = 0;
    for (let k = 1; k <= N; k++) s += Math.sin((2*k-1)*t)/(2*k-1);
    return (4/Math.PI)*s;
  }

  const panels = [
    { N:1, label:'N=1 (1 term)',  color:'#c0392b', x:10  },
    { N:3, label:'N=3 (3 terms)', color:'#e07b00', x:230 },
    { N:7, label:'N=7 (7 terms)', color:'#27ae60', x:450 },
    { N:20,label:'N=20 (20 terms)',color:'#2E75B6',x:670 },
  ];
  const pH = 220, pW = 210, pY = 40;

  function panelGraph({ N, color, x }) {
    const ox2 = x + 30, oy2 = pY + pH/2, scx = (pW-40)/(2*Math.PI*1.05), scy = 85;
    const tx = t => ox2 + t*scx;
    const ty = y => oy2 - y*scy;

    // actual square wave
    const sqPts = [];
    for (let t = 0; t <= 2*Math.PI; t += 0.02) sqPts.push([t, t < Math.PI ? 1 : -1]);
    // partial sum
    const approxPts = [];
    for (let t = 0; t <= 2*Math.PI; t += 0.02) approxPts.push([t, squarePartial(t, N)]);

    return (
      <g>
        <rect x={x} y={pY} width={pW} height={pH} rx={4} fill="#fff" stroke="#ccd9ea"/>
        {/* Axes */}
        <line x1={ox2} y1={ty(-1.4)} x2={ox2} y2={ty(1.4)} stroke="#ddd" strokeWidth="1"/>
        <line x1={tx(0)} y1={oy2} x2={tx(2*Math.PI)} y2={oy2} stroke="#ddd" strokeWidth="1"/>
        {/* Square wave reference */}
        <path d={makePath(sqPts, tx, ty, [pY-2, pY+pH+2])} fill="none" stroke="#ccd9ea" strokeWidth="2" strokeDasharray="5,3"/>
        {/* Partial sum */}
        <path d={makePath(approxPts, tx, ty, [pY-2, pY+pH+2])} fill="none" stroke={color} strokeWidth="2.2"/>
        {/* y=±1 reference lines */}
        <line x1={tx(0)} y1={ty(1)} x2={tx(2*Math.PI)} y2={ty(1)} stroke="#eee" strokeWidth="1"/>
        <line x1={tx(0)} y1={ty(-1)} x2={tx(2*Math.PI)} y2={ty(-1)} stroke="#eee" strokeWidth="1"/>
      </g>
    );
  }

  // Bottom: spectrum bars
  const specY = 280, specH = 80;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Fourier Series — Approximating a Square Wave with Successive Partial Sums</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {panels.map(p => panelGraph(p))}

        {/* Labels */}
        {panels.map(({ N, label, color, x, pW:pw }) => (
          <g key={N}>
            <text x={x + pW/2} y={pY - 8} textAnchor="middle" fontSize="12" fill={color} fontWeight="700">{label}</text>
            <text x={x + pW/2} y={pY + pH + 16} textAnchor="middle" fontSize="10" fill="#888" fontStyle="italic">
              {N===20 ? 'Gibbs phenomenon at jump' : N===1 ? 'Just a sine wave' : 'Getting closer…'}
            </text>
          </g>
        ))}

        {/* Dashed grey = target square wave legend */}
        <line x1={20} y1={specY+10} x2={50} y2={specY+10} stroke="#bbb" strokeWidth="2" strokeDasharray="5,3"/>
        <text x={56} y={specY+14} fontSize="11" fill="#888">target square wave</text>

        {/* Spectrum for N=20: bars at odd harmonics */}
        <text x={W/2} y={specY+6} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Spectrum: coefficients bₙ = 4/(nπ) for odd n, 0 for even n</text>
        {[1,3,5,7,9,11,13,15,17,19].map((n, i) => {
          const coeff = 4/(n*Math.PI);
          const bx = 60 + i * 82;
          const bh = coeff * 140;
          const by = specY + specH + 40 - bh;
          const clr = i < 4 ? ['#c0392b','#e07b00','#27ae60','#2E75B6'][i] : '#9b59b6';
          return (
            <g key={n}>
              <rect x={bx-18} y={by} width={36} height={bh} rx={3} fill={clr} fillOpacity="0.82"/>
              <text x={bx} y={by-5} textAnchor="middle" fontSize="10" fill={clr} fontWeight="700">{coeff.toFixed(2)}</text>
              <text x={bx} y={specY+specH+55} textAnchor="middle" fontSize="10" fill="#555">n={n}</text>
            </g>
          );
        })}
        <line x1={40} y1={specY+specH+40} x2={W-10} y2={specY+specH+40} stroke="#ccd9ea" strokeWidth="1"/>
      </svg>
      <div style={S.dCaption}>A square wave contains only odd harmonics. Adding more terms (N=1 → 20) sharpens the approximation, but a persistent overshoot appears at each jump — the Gibbs phenomenon (~9% of the jump height, regardless of how many terms are added). The spectrum (bottom) shows the coefficients decaying as 1/n: low frequencies dominate, high frequencies add the sharp edges.</div>
    </div>
  );
}

// ── Diagram 3: Transform Pairs ───────────────────────────────────
function DiagramPairs() {
  const W = 900, H = 340;

  // Four classic transform pairs drawn as mini dual-panel graphics
  const pairs = [
    {
      name: 'Rectangular Pulse',
      formula: 'rect(t/τ) ↔ τ·sinc(fτ)',
      color: '#2E75B6', bg: '#EBF3FB',
      left: t => (Math.abs(t) <= 1 ? 1 : 0),
      right: f => { const x = Math.PI*f*2; return Math.abs(x)<0.001 ? 2 : 2*Math.sin(x)/x; },
      lRange:[-3,3], rRange:[-4,4],
    },
    {
      name: 'Gaussian',
      formula: 'e^(−πt²) ↔ e^(−πf²)',
      color: '#27ae60', bg: '#f0fff4',
      left:  t => Math.exp(-Math.PI*t*t),
      right: f => Math.exp(-Math.PI*f*f),
      lRange:[-2.5,2.5], rRange:[-2.5,2.5],
    },
    {
      name: 'Decaying Exponential',
      formula: 'e^(−at)u(t) ↔ 1/(a+j2πf)',
      color: '#e07b00', bg: '#fff7ee',
      left:  t => t>=0 ? Math.exp(-1.5*t) : 0,
      right: f => 1/Math.sqrt(1.5*1.5 + (2*Math.PI*f)*(2*Math.PI*f)),
      lRange:[-0.5,3], rRange:[-3,3],
    },
    {
      name: 'Dirac Delta',
      formula: 'δ(t) ↔ 1  (flat spectrum)',
      color: '#c0392b', bg: '#fff0f0',
      left:  t => (Math.abs(t)<0.08 ? 12 : 0),
      right: f => 1,
      lRange:[-3,3], rRange:[-3,3],
      leftSpecial: true,
    },
  ];

  const pW = 212, pH = 280, gH = 90;

  function miniPanel(pair, xi) {
    const { name, formula, color, bg, left, right, lRange, rRange, leftSpecial } = pair;
    const px = xi * (pW + 8) + 5;
    const py = 20;
    const halfW = (pW - 24) / 2;

    // left mini graph
    const lx0 = px + 6, lx1 = lx0 + halfW;
    const rx0 = lx1 + 12, rx1 = px + pW - 6;
    const gy0 = py + 70, gy1 = gy0 + gH;
    const midY = (gy0 + gy1) / 2;

    function miniPath(fn, range, x0, x1) {
      const scx = (x1 - x0) / (range[1] - range[0]);
      let d = ''; let pen = false;
      for (let v = range[0]; v <= range[1]; v += (range[1]-range[0])/120) {
        const y = fn(v);
        const px2 = x0 + (v - range[0]) * scx;
        const py2 = midY - y * (gH * 0.42);
        if (py2 < gy0 - 4 || py2 > gy1 + 4) { pen = false; continue; }
        d += (pen ? 'L' : 'M') + px2.toFixed(1) + ' ' + py2.toFixed(1) + ' ';
        pen = true;
      }
      return d;
    }

    return (
      <g key={name}>
        <rect x={px} y={py} width={pW} height={pH} rx={6} fill={bg} stroke={color} strokeWidth="2"/>
        <rect x={px} y={py} width={pW} height={38} rx={6} fill={color}/>
        <rect x={px} y={py+26} width={pW} height={12} fill={color}/>
        <text x={px+pW/2} y={py+17} textAnchor="middle" fontSize="11.5" fill="#fff" fontWeight="700">{name}</text>
        <text x={px+pW/2} y={py+50} textAnchor="middle" fontSize="9.5" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{formula}</text>

        {/* Left graph box */}
        <rect x={lx0} y={gy0} width={halfW} height={gH} rx={3} fill="#fff" stroke="#ddd"/>
        <line x1={lx0} y1={midY} x2={lx1} y2={midY} stroke="#eee" strokeWidth="1"/>
        <line x1={(lx0+lx1)/2} y1={gy0} x2={(lx0+lx1)/2} y2={gy1} stroke="#eee" strokeWidth="1"/>
        <text x={(lx0+lx1)/2} y={gy0-4} textAnchor="middle" fontSize="9" fill="#888">time (t)</text>
        {leftSpecial
          ? <line x1={(lx0+lx1)/2} y1={midY} x2={(lx0+lx1)/2} y2={gy0+8} stroke={color} strokeWidth="3" markerEnd="url(#arr2)"/>
          : <path d={miniPath(left, lRange, lx0, lx1)} fill="none" stroke={color} strokeWidth="2"/>
        }

        {/* Arrow between panels */}
        <text x={(lx1+rx0)/2} y={midY+4} textAnchor="middle" fontSize="13" fill="#9b59b6" fontWeight="700">↔</text>

        {/* Right graph box */}
        <rect x={rx0} y={gy0} width={halfW} height={gH} rx={3} fill="#fff" stroke="#ddd"/>
        <line x1={rx0} y1={midY} x2={rx1} y2={midY} stroke="#eee" strokeWidth="1"/>
        <line x1={(rx0+rx1)/2} y1={gy0} x2={(rx0+rx1)/2} y2={gy1} stroke="#eee" strokeWidth="1"/>
        <text x={(rx0+rx1)/2} y={gy0-4} textAnchor="middle" fontSize="9" fill="#888">freq (f)</text>
        <path d={miniPath(right, rRange, rx0, rx1)} fill="none" stroke={color} strokeWidth="2"/>

        {/* Key property */}
        {[
          'Wider pulse → narrower sinc',
          'Gaussian is its own transform!',
          'Magnitude: 1/√(a²+(2πf)²)',
          'Delta → all frequencies equally',
        ][xi].split('→').map((part, k, arr) => (
          <text key={k} x={px+pW/2} y={gy1+22+k*16} textAnchor="middle" fontSize="10" fill="#555" fontStyle={k===0?'italic':'normal'}>
            {k===0 && arr.length>1 ? part+'→' : part}
          </text>
        ))}
      </g>
    );
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Classic Fourier Transform Pairs — Time Domain (left) ↔ Frequency Domain (right)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <marker id="arr2" markerWidth="6" markerHeight="6" refX="3" refY="6" orient="auto">
            <polygon points="0 0, 6 0, 3 6" fill="#c0392b"/>
          </marker>
        </defs>
        {pairs.map((p, i) => miniPanel(p, i))}
      </svg>
      <div style={S.dCaption}>Each pair shows a signal and its Fourier Transform side by side. The duality principle means time and frequency are symmetric: a narrow spike in time gives a wide, flat spectrum (and vice versa). The Gaussian is the unique function equal to its own transform — a fact that underlies the Heisenberg uncertainty principle in quantum mechanics.</div>
    </div>
  );
}

// ── Diagram 4: Convolution Theorem ──────────────────────────────
function DiagramConvolution() {
  const W = 900, H = 330;

  // Show: convolution in time = multiplication in frequency
  // f * g in time ↔ F·G in frequency
  // Use rect * rect = triangle

  const rect = t => Math.abs(t) <= 1 ? 1 : 0;
  const tri  = t => Math.abs(t) <= 2 ? (1 - Math.abs(t)/2) : 0;

  const boxH = 90, boxW = 220;

  function miniPlot(fn, range, x0, y0, w, h, color, label) {
    const scx = w / (range[1]-range[0]);
    const scy = h * 0.82;
    const baseY = y0 + h;
    let d = ''; let pen = false;
    for (let t = range[0]; t <= range[1]; t += (range[1]-range[0])/150) {
      const y = fn(t);
      const px = x0 + (t-range[0])*scx;
      const py = baseY - y*scy;
      if (py < y0-2 || py > baseY+2) { pen=false; continue; }
      d += (pen?'L':'M') + px.toFixed(1)+' '+py.toFixed(1)+' ';
      pen = true;
    }
    return (
      <g>
        <rect x={x0} y={y0} width={w} height={h+4} rx={3} fill="#fff" stroke="#ddd"/>
        <line x1={x0} y1={baseY} x2={x0+w} y2={baseY} stroke="#bbb" strokeWidth="1"/>
        <path d={d} fill="none" stroke={color} strokeWidth="2.2"/>
        <text x={x0+w/2} y={y0-6} textAnchor="middle" fontSize="11" fill={color} fontWeight="700">{label}</text>
      </g>
    );
  }

  // Frequency domain: sinc² for triangle transform
  const sinc2 = f => { const x=Math.PI*f*2; return Math.abs(x)<0.001 ? 4 : 4*Math.sin(x)*Math.sin(x)/(x*x); };
  const sinc1 = f => { const x=Math.PI*f*2; return Math.abs(x)<0.001 ? 2 : 2*Math.sin(x)/x; };

  // Layout
  //  Row 1 (time):   rect   *   rect   =   triangle
  //  Row 2 (freq):   sinc   ×   sinc   =   sinc²
  // Connected by vertical FT arrows

  const row1Y = 28, row2Y = 195;
  const col1X = 10, col2X = 270, col3X = 530;
  const freqCol1X = 10, freqCol2X = 270, freqCol3X = 530;

  const opX = [col2X-38, col2X + boxW-38, col3X + boxW-25];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Convolution Theorem: Convolution in Time ≡ Multiplication in Frequency</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Time row label */}
        <rect x={0} y={row1Y-6} width={8} height={boxH+12} rx={2} fill="#2E75B6"/>
        <text x={14} y={row1Y+boxH/2+4} fontSize="11" fill="#2E75B6" fontWeight="700">TIME</text>

        {/* Freq row label */}
        <rect x={0} y={row2Y-6} width={8} height={boxH+12} rx={2} fill="#c0392b"/>
        <text x={14} y={row2Y+boxH/2+4} fontSize="11" fill="#c0392b" fontWeight="700">FREQ</text>

        {/* Time row */}
        {miniPlot(rect, [-3,3], col1X+45, row1Y, boxW, boxH, '#2E75B6', 'f(t) = rect(t)')}
        {miniPlot(rect, [-3,3], col2X+45, row1Y, boxW, boxH, '#2E75B6', 'g(t) = rect(t)')}
        {miniPlot(tri,  [-3,3], col3X+45, row1Y, boxW, boxH, '#2E75B6', '(f*g)(t) = tri(t)')}

        {/* Operators */}
        <text x={col2X+45-20} y={row1Y+boxH/2+6} textAnchor="middle" fontSize="22" fill="#9b59b6" fontWeight="700">✱</text>
        <text x={col3X+45-22} y={row1Y+boxH/2+6} textAnchor="middle" fontSize="22" fill="#555" fontWeight="700">=</text>

        {/* FT arrows */}
        {[col1X+45+boxW/2, col2X+45+boxW/2, col3X+45+boxW/2].map((cx, i) => (
          <g key={i}>
            <line x1={cx} y1={row1Y+boxH+8} x2={cx} y2={row2Y-8} stroke="#9b59b6" strokeWidth="1.8" strokeDasharray="4,3" markerEnd="url(#arr)"/>
            <text x={cx+5} y={(row1Y+boxH+row2Y)/2+4} fontSize="10" fill="#9b59b6">FT</text>
          </g>
        ))}

        {/* Freq row */}
        {miniPlot(sinc1, [-4,4], col1X+45, row2Y, boxW, boxH, '#c0392b', 'F(f) = sinc(f)')}
        {miniPlot(sinc1, [-4,4], col2X+45, row2Y, boxW, boxH, '#c0392b', 'G(f) = sinc(f)')}
        {miniPlot(sinc2, [-4,4], col3X+45, row2Y, boxW, boxH, '#c0392b', 'F(f)·G(f) = sinc²(f)')}

        {/* Operators freq row */}
        <text x={col2X+45-20} y={row2Y+boxH/2+6} textAnchor="middle" fontSize="22" fill="#c0392b" fontWeight="700">×</text>
        <text x={col3X+45-22} y={row2Y+boxH/2+6} textAnchor="middle" fontSize="22" fill="#555" fontWeight="700">=</text>

        {/* Key insight box */}
        <rect x={col3X+45+boxW+18} y={row1Y} width={215} height={H-row1Y-10} rx={5} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={col3X+45+boxW+18+107} y={row1Y+20} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Why This Matters</text>
        {[
          {y:50, bc:'#2E75B6', t1:'Computation', t2:'Convolution of N-point signals costs O(N²)'},
          {y:115, bc:'#27ae60', t1:'FFT shortcut', t2:'FT, multiply, inverse FT costs O(N log N)'},
          {y:180, bc:'#c0392b', t1:'Applications', t2:'Filters, image blur/sharpen, LTI systems'},
          {y:245, bc:'#9b59b6', t1:'Duality', t2:'Multiplication in time ↔ convolution in freq'},
        ].map(({y, bc, t1, t2}) => (
          <g key={t1}>
            <rect x={col3X+45+boxW+26} y={row1Y+y} width={199} height={57} rx={3} fill="#fff" stroke={bc} strokeWidth="1.2"/>
            <text x={col3X+45+boxW+36} y={row1Y+y+18} fontSize="11" fill="#1F3864" fontWeight="700">{t1}</text>
            <text x={col3X+45+boxW+36} y={row1Y+y+36} fontSize="10" fill="#555">{t2}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The convolution theorem is why the Fourier Transform is so useful in engineering. Computing convolution directly is slow (O(N²)); but transforming both signals, multiplying pointwise, then inverting is fast (O(N log N) via the FFT). This trick underlies digital audio filters, image processing kernels, radar signal processing, and the multiplication of large numbers.</div>
    </div>
  );
}

// ── Diagram 5: DFT and FFT ───────────────────────────────────────
function DiagramDFT() {
  const W = 900, H = 320;

  // DFT of a sampled signal — show N=16 samples of a 2-Hz + 5-Hz mixture
  const N = 16;
  const fs = 16; // sampling freq
  const samples = Array.from({length:N}, (_,n) => Math.sin(2*Math.PI*2*n/fs) + 0.6*Math.sin(2*Math.PI*5*n/fs));

  // Compute DFT magnitudes
  const mag = Array.from({length:N}, (_,k) => {
    let re = 0, im = 0;
    for (let n = 0; n < N; n++) {
      re += samples[n]*Math.cos(2*Math.PI*k*n/N);
      im -= samples[n]*Math.sin(2*Math.PI*k*n/N);
    }
    return Math.sqrt(re*re+im*im)/N;
  });

  // Left: sampled time signal
  const lX=10, lW=390, lH=220, lY=50;
  const ox=lX+50, oy=lY+lH/2+20, scx=(lW-60)/(N-1), scy=70;
  const tx=n=>ox+n*scx, ty=y=>oy-y*scy;

  // Right: DFT spectrum (first N/2+1 unique bins)
  const rX=470, rW=420, rH=220, rY=50;
  const fox=rX+50, foy=rY+rH-10, fscx=(rW-60)/(N/2), fscy=160;
  const ftx=k=>fox+k*fscx, fty=a=>foy-a*fscy;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Discrete Fourier Transform (DFT) — N=16 Samples of sin(2π·2t) + 0.6·sin(2π·5t)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left panel */}
        <rect x={lX} y={lY-20} width={lW} height={lH+20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={lX+lW/2} y={lY-28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Sampled Signal x[n]</text>

        {/* Axes */}
        <line x1={ox} y1={ty(-1.6)} x2={ox} y2={ty(1.8)} stroke="#1F3864" strokeWidth="1.3"/>
        <line x1={tx(0)-10} y1={oy} x2={tx(N-1)+10} y2={oy} stroke="#1F3864" strokeWidth="1.3"/>
        <text x={tx(N-1)+14} y={oy+4} fontSize="12" fill="#1F3864" fontWeight="700">n</text>
        <text x={ox+4} y={ty(1.8)-4} fontSize="12" fill="#1F3864" fontWeight="700">x[n]</text>

        {/* Connect samples */}
        {samples.map((v,n)=>n>0 &&
          <line key={n} x1={tx(n-1)} y1={ty(samples[n-1])} x2={tx(n)} y2={ty(v)} stroke="#ccd9ea" strokeWidth="1.2" strokeDasharray="3,2"/>
        )}

        {/* Sample stems */}
        {samples.map((v,n)=>(
          <g key={n}>
            <line x1={tx(n)} y1={oy} x2={tx(n)} y2={ty(v)} stroke="#2E75B6" strokeWidth="1.8"/>
            <circle cx={tx(n)} cy={ty(v)} r={4} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
            <text x={tx(n)} y={oy+14} textAnchor="middle" fontSize="9" fill="#bbb">{n}</text>
          </g>
        ))}

        {/* Right panel */}
        <rect x={rX} y={lY-20} width={rW} height={lH+20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={rX+rW/2} y={lY-28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">DFT Spectrum |X[k]|</text>

        {/* Freq axes */}
        <line x1={fox} y1={fty(0)} x2={fox} y2={fty(1.1)} stroke="#1F3864" strokeWidth="1.3"/>
        <line x1={fox-10} y1={foy} x2={ftx(N/2)+10} y2={foy} stroke="#1F3864" strokeWidth="1.3"/>
        <text x={ftx(N/2)+14} y={foy+4} fontSize="12" fill="#1F3864" fontWeight="700">k</text>
        <text x={fox+4} y={fty(1.1)-4} fontSize="12" fill="#1F3864" fontWeight="700">|X[k]|</text>

        {/* DFT bars — first N/2+1 */}
        {mag.slice(0, N/2+1).map((m, k) => {
          const bx=ftx(k), by=fty(m);
          const clr = (k===2||k===5) ? '#c0392b' : '#2E75B6';
          const opacity = m > 0.3 ? 1 : 0.45;
          return (
            <g key={k}>
              <line x1={bx} y1={foy} x2={bx} y2={by} stroke={clr} strokeWidth="14" strokeOpacity={opacity}/>
              <text x={bx} y={foy+13} textAnchor="middle" fontSize="9" fill="#888">{k}</text>
              {m > 0.15 && <text x={bx} y={by-6} textAnchor="middle" fontSize="9.5" fill={clr} fontWeight="700">{m.toFixed(2)}</text>}
            </g>
          );
        })}

        {/* Highlight labels */}
        <text x={ftx(2)+2} y={fty(mag[2])-18} fontSize="11" fill="#c0392b" fontWeight="700">k=2 (2 Hz)</text>
        <text x={ftx(5)+2} y={fty(mag[5])-18} fontSize="11" fill="#c0392b" fontWeight="700">k=5 (5 Hz)</text>

        {/* FFT complexity note */}
        <rect x={lX} y={lY+lH+14} width={W-20} height={60} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={lX+14} y={lY+lH+32} fontSize="12" fill="#1F3864" fontWeight="700">DFT formula: </text>
        <text x={lX+110} y={lY+lH+32} fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace">X[k] = Σₙ x[n]·e^(−j2πkn/N),  k=0,1,…,N−1</text>
        <text x={lX+14} y={lY+lH+52} fontSize="11" fill="#555">Direct computation: O(N²) multiplications.   FFT (Cooley-Tukey) exploits symmetry of e^(−j2πkn/N) to achieve O(N log₂ N) — a factor of N/log₂N faster (for N=1024, that is ~100×).</text>
      </svg>
      <div style={S.dCaption}>The DFT maps N discrete time samples to N frequency bins spaced fs/N Hz apart. Here N=16, fs=16 Hz, so bin k corresponds to frequency k·1 Hz. The two signal components appear precisely at k=2 and k=5, with amplitudes 0.50 and 0.30 (half the time-domain amplitudes due to the two-sided spectrum). The FFT computes the DFT in O(N log N) rather than O(N²).</div>
    </div>
  );
}

// ── Diagram 6: Properties Visual Summary ────────────────────────
function DiagramProperties() {
  const W = 900, H = 290;

  const props = [
    {
      title:'Linearity', color:'#2E75B6', bg:'#EBF3FB',
      formula:'FT{af+bg} = aF+bG',
      desc:'Superposition: transform a sum by summing transforms. Enables analysis of each component independently.',
      icon: (x,y) => <>
        <line x1={x+10} y1={y+40} x2={x+60} y2={y+40} stroke="#2E75B6" strokeWidth="2"/>
        <line x1={x+10} y1={y+55} x2={x+60} y2={y+55} stroke="#27ae60" strokeWidth="2" strokeDasharray="4,2"/>
        <text x={x+32} y={y+72} textAnchor="middle" fontSize="16" fill="#555">+</text>
        <text x={x+70} y={y+72} textAnchor="middle" fontSize="16" fill="#555">→</text>
      </>,
    },
    {
      title:'Time Shifting', color:'#27ae60', bg:'#f0fff4',
      formula:'FT{f(t−t₀)} = e^(−j2πft₀)·F(f)',
      desc:'Delaying a signal by t₀ multiplies its spectrum by a phase factor. Magnitude unchanged; only phase shifts.',
      icon: (x,y) => <>
        <path d={`M${x+8},${y+55} Q${x+30},${y+20} ${x+52},${y+55}`} fill="none" stroke="#ccd9ea" strokeWidth="1.5" strokeDasharray="3,2"/>
        <path d={`M${x+22},${y+55} Q${x+44},${y+20} ${x+66},${y+55}`} fill="none" stroke="#27ae60" strokeWidth="2"/>
        <text x={x+36} y={y+72} textAnchor="middle" fontSize="10" fill="#27ae60">← shift →</text>
      </>,
    },
    {
      title:'Frequency Shifting', color:'#e07b00', bg:'#fff7ee',
      formula:'FT{e^(j2πf₀t)f(t)} = F(f−f₀)',
      desc:'Multiplying by a complex exponential (modulation) shifts the spectrum. Foundation of AM radio and heterodyning.',
      icon: (x,y) => <>
        <line x1={x+12} y1={y+55} x2={x+62} y2={y+55} stroke="#e07b00" strokeWidth="1"/>
        {[20,32,44].map(bx=>(
          <g key={bx}>
            <line x1={x+bx} y1={y+55} x2={x+bx} y2={y+30} stroke="#e07b00" strokeWidth="6" strokeOpacity="0.7"/>
          </g>
        ))}
        <text x={x+36} y={y+72} textAnchor="middle" fontSize="10" fill="#e07b00">shift in freq</text>
      </>,
    },
    {
      title:'Scaling', color:'#c0392b', bg:'#fff0f0',
      formula:'FT{f(at)} = (1/|a|)·F(f/a)',
      desc:'Compressing time (a>1) stretches frequency, and vice versa. Narrower pulse → wider bandwidth.',
      icon: (x,y) => <>
        <rect x={x+20} y={y+32} width={20} height={20} rx={2} fill="none" stroke="#ccd9ea" strokeWidth="1.5" strokeDasharray="3,2"/>
        <rect x={x+12} y={y+36} width={36} height={12} rx={2} fill="none" stroke="#c0392b" strokeWidth="2"/>
        <text x={x+36} y={y+72} textAnchor="middle" fontSize="10" fill="#c0392b">wider ↔ narrower</text>
      </>,
    },
    {
      title:'Parseval\'s Theorem', color:'#9b59b6', bg:'#f5d0ff',
      formula:'∫|f(t)|²dt = ∫|F(f)|²df',
      desc:'Total energy is preserved by the FT. Energy can be computed in either domain — crucial for spectral energy analysis.',
      icon: (x,y) => <>
        <text x={x+36} y={y+38} textAnchor="middle" fontSize="13" fill="#9b59b6">∫|f|²</text>
        <text x={x+36} y={y+54} textAnchor="middle" fontSize="13" fill="#9b59b6">= ∫|F|²</text>
        <text x={x+36} y={y+70} textAnchor="middle" fontSize="10" fill="#9b59b6">energy preserved</text>
      </>,
    },
    {
      title:'Differentiation', color:'#1F3864', bg:'#e8ecf4',
      formula:"FT{f'(t)} = j2πf · F(f)",
      desc:'Differentiation in time → multiply by j2πf in frequency. Converts ODEs to algebraic equations.',
      icon: (x,y) => <>
        <text x={x+36} y={y+38} textAnchor="middle" fontSize="12" fill="#1F3864">d/dt</text>
        <text x={x+36} y={y+54} textAnchor="middle" fontSize="12" fill="#1F3864">→ ×j2πf</text>
        <text x={x+36} y={y+70} textAnchor="middle" fontSize="10" fill="#1F3864">algebraic!</text>
      </>,
    },
  ];

  const cW = 142, cH = 260, margin = 8;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Key Properties of the Fourier Transform</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {props.map(({ title, color, bg, formula, desc, icon }, i) => {
          const x = i*(cW+margin) + 4;
          const y = 10;
          return (
            <g key={title}>
              <rect x={x} y={y} width={cW} height={cH} rx={6} fill={bg} stroke={color} strokeWidth="2"/>
              <rect x={x} y={y} width={cW} height={36} rx={6} fill={color}/>
              <rect x={x} y={y+24} width={cW} height={12} fill={color}/>
              <text x={x+cW/2} y={y+22} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{title}</text>
              <rect x={x+6} y={y+44} width={cW-12} height={30} rx={3} fill="#fff" fillOpacity="0.7"/>
              <text x={x+cW/2} y={y+61} textAnchor="middle" fontSize="9.5" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{formula}</text>
              {icon(x, y+80)}
              <foreignObject x={x+6} y={y+162} width={cW-12} height={90}>
                <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontSize:'9.5px', color:'#444', lineHeight:'1.45' }}>{desc}</div>
              </foreignObject>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>These six properties let you manipulate Fourier Transforms without re-computing the integral. Linearity and shifting handle most signal processing tasks; differentiation converts differential equations to algebra; Parseval's theorem audits energy budgets; scaling encodes the time-bandwidth trade-off that governs every communications system.</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function FourierTransforms() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>The Fourier Transform</h1>
      <p style={S.p}>The Fourier Transform is one of the most powerful tools in mathematics, converting a function of time (or space) into a function of frequency. Every signal — sound, light, radio wave, seismic tremor — is secretly a sum of sinusoids, and the Fourier Transform reveals exactly which frequencies are present and at what strength. Developed by Joseph Fourier in 1822 to study heat conduction, the transform now underpins digital audio, image compression (JPEG), MRI scanners, mobile phone networks, quantum mechanics, and virtually every corner of engineering and physics.</p>

      <InfoBox title="Core Idea: Any signal is a superposition of sinusoids at different frequencies" items={[
        "Forward FT: F(f) = ∫ f(t) e^(−j2πft) dt  — decomposes f(t) into its frequency content",
        "Inverse FT: f(t) = ∫ F(f) e^(+j2πft) df  — reconstructs the signal from its spectrum",
        "F(f) is complex: |F(f)| is the amplitude spectrum, arg(F(f)) is the phase spectrum",
        "Fourier Series: periodic signals → discrete spectrum (harmonics at integer multiples of f₀)",
        "Fourier Transform: aperiodic signals → continuous spectrum",
        "DFT/FFT: discrete, finite signals → discrete spectrum; computed in O(N log N)",
      ]}/>

      <h2 style={S.h2}>Time Domain vs Frequency Domain</h2>
      <DiagramTimeFreq />
      <Table2 rows={[
        ["Forward FT","F(f) = ∫₋∞^∞ f(t) e^(−j2πft) dt\nProjects f(t) onto complex exponentials; F(f) is the 'weight' at frequency f"],
        ["Inverse FT","f(t) = ∫₋∞^∞ F(f) e^(+j2πft) df\nReassembles the signal; the two transforms are near-symmetric"],
        ["Angular frequency ω","ω = 2πf (rad/s)\nAlternate convention: F(ω) = ∫ f(t)e^(−jωt)dt (factor of 2π moves around)"],
        ["Amplitude spectrum","|F(f)| — how much of frequency f is present\nPlot vs f to see signal's 'fingerprint'"],
        ["Phase spectrum","arg(F(f)) — phase offset of the sinusoid at frequency f\nOften ignored visually but critical for waveform shape"],
        ["Euler's formula","e^(jθ) = cos θ + j sin θ\nLinks complex exponentials to sines and cosines — the bridge between the two domains"],
        ["Reality condition","If f(t) is real, then F(−f) = F*(f) (conjugate symmetry)\nOnly the positive-frequency half is independent"],
      ]}/>

      <h2 style={S.h2}>Fourier Series and Convergence</h2>
      <DiagramFourierSeries />
      <Table2 rows={[
        ["Fourier series (periodic)","f(t) = a₀/2 + Σ[aₙcos(nω₀t) + bₙsin(nω₀t)],  ω₀=2π/T\nDecomposes any periodic function into harmonics"],
        ["Cosine coefficients aₙ","aₙ = (2/T)∫₀ᵀ f(t)cos(nω₀t)dt\nProjection onto cosine at n-th harmonic"],
        ["Sine coefficients bₙ","bₙ = (2/T)∫₀ᵀ f(t)sin(nω₀t)dt"],
        ["Complex form","f(t) = Σ cₙ e^(jnω₀t),   cₙ = (1/T)∫₀ᵀ f(t)e^(−jnω₀t)dt\nMore compact; cₙ = (aₙ−jbₙ)/2 for n>0"],
        ["Square wave spectrum","Only odd harmonics: bₙ=4/(nπ) for odd n, 0 for even n\nAmplitudes decay as 1/n — slow, meaning sharp edges need many terms"],
        ["Gibbs phenomenon","~9% overshoot at jump discontinuities, regardless of how many terms\nNot a failure of the series — it converges in L² norm, not uniformly"],
        ["Convergence (Dirichlet)","Converges to f(t) where continuous; to average of left/right limits at jumps\nRequires: f piecewise smooth, finite number of discontinuities per period"],
        ["Parseval (series)","(1/T)∫|f(t)|²dt = Σ|cₙ|²\nTotal power = sum of powers in each harmonic"],
      ]}/>

      <h2 style={S.h2}>Classic Transform Pairs</h2>
      <DiagramPairs />
      <Table2 rows={[
        ["Rectangular pulse","rect(t/τ) ↔ τ·sinc(fτ),   sinc(x) = sin(πx)/(πx)\nWider pulse → narrower sinc mainlobe (time-bandwidth product is constant)"],
        ["Gaussian","e^(−πt²) ↔ e^(−πf²)\nUnique self-dual function; minimises time-bandwidth product (uncertainty principle)"],
        ["Decaying exponential","e^(−at)u(t) ↔ 1/(a+j2πf),   magnitude 1/√(a²+(2πf)²)\nPole at s=−a; Lorentzian spectral shape"],
        ["Dirac delta","δ(t) ↔ 1 (flat spectrum — all frequencies equally)\nInfinitely narrow spike in time requires infinite bandwidth"],
        ["Constant","1 ↔ δ(f) (spike at DC)\nDual of the above; duality: FT{FT{f(t)}} = f(−t)"],
        ["Sinusoid","cos(2πf₀t) ↔ ½[δ(f−f₀)+δ(f+f₀)]\nTwo spikes at ±f₀; a pure tone in time is a pure spike in frequency"],
        ["Shah / comb","Σδ(t−nT) ↔ (1/T)Σδ(f−n/T)\nSampling in time ↔ periodicity in frequency; basis of sampling theorem"],
        ["Time-bandwidth product","Δt · Δf ≥ 1/(4π)   (uncertainty principle)\nCannot simultaneously have arbitrarily fine time and frequency resolution"],
      ]}/>

      <h2 style={S.h2}>The Convolution Theorem</h2>
      <DiagramConvolution />
      <Table2 rows={[
        ["Convolution (time)","(f*g)(t) = ∫ f(τ)g(t−τ)dτ\nSlide g over f, integrating the overlap at each delay τ"],
        ["Convolution theorem","FT{f*g} = F(f)·G(f)\nConvolution in time ↔ pointwise multiplication in frequency"],
        ["Dual (correlation)","FT{f·g} = F * G   (convolution in frequency)\nMultiplication in time ↔ convolution in frequency"],
        ["Cross-correlation","(f⋆g)(τ) = ∫ f*(t)g(t+τ)dt\nMeasures similarity as a function of lag; used in radar, GPS, audio fingerprinting"],
        ["LTI systems","Output = input * impulse response h(t)\nIn frequency: Y(f) = X(f)·H(f) — multiply spectra, no integral needed"],
        ["Transfer function H(f)","H(f) = Y(f)/X(f) — the system's gain and phase at each frequency\nDesign filters by shaping H(f) then taking inverse FT"],
        ["FFT-based convolution","Transform both signals, multiply, inverse transform\nO(N log N) vs O(N²) direct; practical for N > ~64"],
        ["Circular vs linear","DFT convolution is circular (periodic); zero-pad to N≥L+M−1 for linear result"],
      ]}/>

      <h2 style={S.h2}>The Discrete Fourier Transform and FFT</h2>
      <DiagramDFT />
      <Table2 rows={[
        ["DFT definition","X[k] = Σₙ₌₀^(N-1) x[n]·e^(−j2πkn/N),  k=0,…,N−1\nN input samples → N complex output bins"],
        ["Frequency resolution","Δf = fs/N  (Hz per bin)\nTo resolve Δf Hz, need at least N=fs/Δf samples"],
        ["Bin k corresponds to","Frequency k·(fs/N) Hz\nBins N/2+1 to N−1 are the mirror (negative frequencies for real signals)"],
        ["Inverse DFT","x[n] = (1/N)Σₖ X[k]·e^(+j2πkn/N)\nRecovers the time samples from the spectrum"],
        ["FFT (Cooley-Tukey)","Recursively splits N-point DFT into two N/2-point DFTs\nO(N log₂N) multiplications vs O(N²) for direct DFT"],
        ["Spectral leakage","If signal frequency is not exactly on a bin, energy spreads to neighbouring bins\nWindow functions (Hann, Hamming, Blackman) reduce leakage at cost of resolution"],
        ["Zero-padding","Pad x[n] with zeros to increase N → finer frequency grid\nImproves interpolation of spectrum, not true resolution"],
        ["Sampling theorem (Nyquist)","Must sample at fs ≥ 2·fmax to avoid aliasing\nFrequencies above fs/2 fold back and corrupt the spectrum"],
      ]}/>

      <h2 style={S.h2}>Key Properties</h2>
      <DiagramProperties />
      <Table2 rows={[
        ["Linearity","FT{af(t)+bg(t)} = aF(f)+bG(f)\nTransform a sum by summing transforms"],
        ["Time shift","FT{f(t−t₀)} = e^(−j2πft₀)·F(f)\nDelay → phase shift; amplitude spectrum unchanged"],
        ["Frequency shift","FT{e^(j2πf₀t)f(t)} = F(f−f₀)\nModulation (multiply by complex exponential) → translation in frequency"],
        ["Time scaling","FT{f(at)} = (1/|a|)·F(f/a)\nCompress in time → expand in frequency; time-bandwidth product is preserved"],
        ["Differentiation","FT{f'(t)} = j2πf·F(f)\nEach derivative multiplies by j2πf; converts ODEs to algebraic equations in F"],
        ["Integration","FT{∫f dt} = F(f)/(j2πf)  (for F(0)=0)\nIntegration divides by j2πf"],
        ["Parseval's theorem","∫|f(t)|²dt = ∫|F(f)|²df\nEnergy is conserved by the transform"],
        ["Duality","FT{F(t)} = f(−f)\nSwapping time and frequency gives the mirror signal; rect↔sinc is a classic example"],
        ["Convolution theorem","FT{f*g} = F·G  and  FT{f·g} = F*G\nMost useful property in signal processing"],
      ]}/>

      <InfoBox title="Key Takeaway — Which Tool for Which Situation?" items={[
        "Periodic signal, need harmonics? → Fourier Series (discrete spectrum, coefficients aₙ, bₙ)",
        "Aperiodic signal, continuous spectrum? → Continuous Fourier Transform F(f)=∫f(t)e^(−j2πft)dt",
        "Discrete time samples, finite record? → DFT / FFT (bin resolution = fs/N Hz)",
        "Convolving two signals? → FFT both, multiply pointwise, IFFT (O(N log N) vs O(N²))",
        "LTI system analysis? → Transfer function H(f)=Y(f)/X(f); design filter in freq domain",
        "Sharp spike in time? → Wide flat spectrum; narrow pulse needs large bandwidth",
        "Self-similar in time and freq? → Gaussian: FT{e^(−πt²)}=e^(−πf²)",
        "Spectral leakage in DFT? → Apply Hann/Hamming window before FFT",
        "Solving an ODE with FT? → Differentiation rule converts to algebra: d/dt → ×(j2πf)",
      ]}/>
    </div>
  );
}